// src/components/AirportInput/AirportInput.tsx
import React, { useState, useEffect, useRef } from "react";
import Autocomplete from "./Autocomplete";
import axios from "axios";
import { AutocompleteItem } from "@/types/autocomplete";

const AIRPORT_API_ENDPOINT =
  "https://api-zneith.zdev.tech/api-air-0.1/autocomplete/airports/search?language=es&query=";

interface AirportInputProps {
  value?: any;
  //defaultValue?: { cityName: string };
  placeholder?: string;
  identifier: string;
  onSelectAirport: (data: { location: any; type: string }) => void;
}

const AirportInput: React.FC<AirportInputProps> = ({
  //defaultValue,
  value,
  placeholder,
  identifier,
  onSelectAirport,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [airports, setAirports] = useState<AutocompleteItem[] | null>(null);
  const [focused, setFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSelected, setIsSelected] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (value) {
      setInputValue(value.cityName);
      setIsSelected(true);
    } else {
      setInputValue("");
      setIsSelected(false);
    }
  }, [value]);


  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (focused && containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setFocused(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [focused]);

  const search = async (val: string) => {
    const res = await axios.get(`${AIRPORT_API_ENDPOINT}${val}`);
    const results: AutocompleteItem[] = res.data.airports.map((airport: any) => {
      let icon: React.ReactNode;
      switch (airport.type) {
        case "C":
          icon = <i className="Autocomplete__icon fa fa-globe" />;
          break;
        case "A":
          icon = <i className="Autocomplete__icon fa fa-plane" />;
          break;
        default:
          icon = null;
      }
      return {
        value: airport.code,
        displayValue: airport.name,
        icon,
        data: airport,
      };
    });
    return results;
  };

  const handleChange = async (val: string) => {
    setInputValue(val);
    setIsSelected(false);

    if (val.length > 2) {
      setIsLoading(true);
      const results = await search(val);
      setAirports(results);
      setFocused(true);
      setIsSelected(false);
      setIsLoading(false);
    } else {
      setAirports(null);
      setFocused(true);
      setIsLoading(false);
      setIsSelected(false);
    }
  };

  const handleSelect = (airport: any) => {
    setInputValue(airport.cityName);
    setFocused(false);
    setIsSelected(true);
    onSelectAirport({ location: airport, type: identifier });
  };

  const handleClose = () => {
    setFocused(false);
  };

  return (
    <div className="AirportInput relative" ref={containerRef}>
      <input
        ref={inputRef}
        type="text"
        className="w-full px-4 py-3 border-2 border-border rounded-lg focus:border-secondary focus:outline-none transition-colors bg-background text-foreground"
        id={identifier}
        autoComplete="off"
        value={inputValue}
        placeholder={placeholder}
        onChange={e => handleChange(e.target.value)}
        onFocus={() => setFocused(true)}
      />

      {focused && (
        <Autocomplete
          items={airports || []}
          isLoading={isLoading}
          onSelect={handleSelect}
          query={inputValue}
          onClose={handleClose}
          anchorRef={inputRef}
        />
      )}
    </div>
  );
};

export default AirportInput;